import React from "react";
import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";

// =======================
// STYLES
// =======================
const styles = StyleSheet.create({
  page: {
    fontSize: 10,
    fontFamily: "Helvetica",
    flexDirection: "column",
    padding: 0,
  },
  table: {
    display: "table",
    width: "auto",
    marginTop: 5,
  },
  tableRow: {
    flexDirection: "row",
  },
  firstCol: { borderLeftWidth: 1 },
  topBorder: { borderTopWidth: 1 },
  tableCol: {
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#000",
    borderTopWidth: 0,
    borderLeftWidth: 0,
    padding: 4,
    flexWrap: "wrap",
    wordBreak: "break-word",
  },
  tableColHeader: {
    backgroundColor: "#e0e0e0",
    fontWeight: "bold",
    textAlign: "center",
  },
  totalRow: {
    backgroundColor: "#b3b3b3",
    fontWeight: "bold",
    padding: 4,
  },
  textLeft: { textAlign: "left" },
  textRight: { textAlign: "right" },
  headerImage: { padding: 2 },
});

// =======================
// HEADER COMPONENT
// =======================
let apiPaths_from_local_storage = localStorage.getItem("apiPaths")
  ? JSON.parse(localStorage.getItem("apiPaths"))
  : "-";

const Header = ({ parentDetails }) => (
  <View style={{ width: "100%" }} fixed>
    <View style={{ padding: 10 }}>
      <View style={{ width: "100%", flexDirection: "column" }}>
        {apiPaths_from_local_storage[0]?.REP_HEADER?.includes("data:image/") && (
          <Image
            src={apiPaths_from_local_storage[0].REP_HEADER}
            style={styles.headerImage}
          />
        )}
        <View style={{ textAlign: "center", marginTop: 10 }}>
          <Text style={{ fontSize: 12, fontWeight: "bold", marginTop: 5 }}>
            Profit & Loss (Month)
          </Text>
        </View>
      </View>
    </View>
  </View>
);

// =======================
// FOOTER COMPONENT
// =======================
const Footer = () => (
  <View style={{ width: "100%" }} fixed>
    <View style={{ width: "100%", padding: 10 }}>
      <View style={{ width: "100%", fontSize: 10 }}>
        <Text
          style={{ textAlign: "center" }}
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages}`
          }
        />
      </View>
    </View>
  </View>
);

// =======================
// TABLE CONTENT COMPONENT
// =======================
const TableContent = ({ data }) => {
  if (!data?.length) {
    return (
      <View style={{ padding: 20, textAlign: "center" }}>
        <Text>No data available</Text>
      </View>
    );
  }

  const columns = Object.keys(data[0] || {});
  const colFlex = columns.map(() => 1);

  // Compute Grand Totals for numeric columns
  const numericCols = columns.filter(
    (col) => typeof data[0][col] === "number" || !isNaN(data[0][col])
  );

  const grandTotals = {};
  numericCols.forEach((col) => {
    grandTotals[col] = data.reduce(
      (sum, row) => sum + (parseFloat(row[col]) || 0),
      0
    );
  });

  return (
    <View style={{ flexGrow: 1 }} wrap>
      <View style={{ padding: 10 }}>
        {/* Table Header */}
        <View style={styles.tableRow} fixed>
          {columns.map((col, i) => (
            <View
              key={i}
              style={[
                styles.tableCol,
                styles.tableColHeader,
                styles.topBorder,
                i === 0 && styles.firstCol,
                { flex: colFlex[i] },
              ]}
            >
              <Text>{col}</Text>
            </View>
          ))}
        </View>

        {/* Table Rows */}
        {data.map((row, ri) => (
          <View key={ri} style={styles.tableRow} wrap={false}>
            {columns.map((col, ci) => (
              <View
                key={ci}
                style={[
                  styles.tableCol,
                  ci === 0 && styles.firstCol,
                  { flex: colFlex[ci] },
                ]}
              >
                <Text
                  style={
                    typeof row[col] === "number"
                      ? styles.textRight
                      : styles.textLeft
                  }
                >
                  {row[col] !== null && row[col] !== undefined
                    ? typeof row[col] === "number"
                      ? row[col].toFixed(2)
                      : row[col].toString()
                    : "-"}
                </Text>
              </View>
            ))}
          </View>
        ))}

        {/* Grand Total Row */}
        <View style={styles.tableRow} wrap={false}>
          {columns.map((col, i) => {
            let content = "";
            if (i === 0) content = "Grand Total:";
            else if (numericCols.includes(col))
              content = grandTotals[col].toFixed(2);

            return (
              <View
                key={i}
                style={[
                  styles.tableCol,
                  styles.totalRow,
                  i === 0 && styles.firstCol,
                  { flex: colFlex[i] },
                ]}
              >
                <Text
                  style={
                    numericCols.includes(col)
                      ? styles.textRight
                      : styles.textLeft
                  }
                >
                  {content}
                </Text>
              </View>
            );
          })}
        </View>
      </View>
    </View>
  );
};

// =======================
// MAIN PDF COMPONENT
// =======================
const ProfitLossMonthPDF = ({ data = [], parentDetails }) => {
  

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        <Header parentDetails={parentDetails} />
        <TableContent data={data} />
        <Footer />
      </Page>
    </Document>
  );
};

export default ProfitLossMonthPDF;
